import type { HeadersFunction, LoaderFunctionArgs } from '@remix-run/node'
import { Link, NavLink, Outlet, useLoaderData } from '@remix-run/react'
import cn from 'classnames'

import { findChapter, findNextChapter } from '@/models/course.server'
import { formatDuration } from '@/utils/number'

type LoaderData = typeof loader

export async function loader({ params }: LoaderFunctionArgs) {
  const chapter = findChapter(params.course as string, params.chapter as string)

  if (!chapter) {
    throw new Response('Not Found', { status: 404 })
  }

  const nextChapter = findNextChapter(params.course as string, chapter)

  return {
    chapter: chapter,
    nextChapter: nextChapter
  }
}

export const headers: HeadersFunction = ({ parentHeaders }) => ({
  'Cache-Control': parentHeaders.get('Cache-Control') ?? ''
})

export default function CourseChapter() {
  const { chapter, nextChapter } = useLoaderData<LoaderData>()

  return (
    <div className='relative grid flex-1 grid-cols-12'>
      <div className='col-span-12 hidden max-w-[400px] flex-1 flex-grow flex-col space-y-3 overflow-y-auto overflow-x-hidden sm:col-span-4 sm:pb-6 sm:pl-4 md:col-span-3 md:flex lg:sticky lg:top-0 lg:h-screen'>
        <nav className='space-y-6'>
          <ol className='list-inside space-y-1'>
            {chapter.lessons.map((lesson) => (
              <li className='group relative' key={lesson.path}>
                <NavLink
                  className={({ isActive }) =>
                    cn('relative block h-auto p-2.5 font-medium transition hover:bg-secondary', {
                      'bg-secondary text-primary': isActive,
                      'text-secondary': !isActive
                    })
                  }
                  prefetch='intent'
                  to={lesson.path}
                >
                  {({ isActive }) => (
                    <>
                      <div className={cn('relative mt-sm', {})}>
                        <span
                          className={cn('font-semibold lg:text-primary', {
                            'text-primary': isActive
                          })}
                        >
                          <span className={cn({ 'text-orange': isActive, 'text-secondary': !isActive })}>
                            {lesson.sequence}.
                          </span>{' '}
                          {lesson.title}
                        </span>{' '}
                      </div>

                      <p className='mt-1 hidden font-normal text-secondary lg:block'>{lesson.description}</p>
                      <p className='mt-1 font-normal tracking-tight text-secondary'>
                        {formatDuration(lesson.video.duration)}
                      </p>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ol>
        </nav>

        {nextChapter && (
          <Link className='btn btn-secondary block max-w-full py-1' prefetch='intent' to={nextChapter.path}>
            <span className='block lg:inline'>Next: </span>
            {nextChapter.title} →
          </Link>
        )}
      </div>

      <div className='relative col-span-12 flex flex-col justify-between gap-y-6 space-y-8 px-4 sm:px-5 sm:pb-5 md:col-span-9 lg:px-6 lg:pb-6'>
        <Outlet />
      </div>
    </div>
  )
}
